/* SPOOKY CART — core theme behaviour (vanilla, no dependencies) */
(function () {
  'use strict';

  const S = window.Spooky || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ------------------------------------------------- */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  function formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const format = S.moneyFormat || '${{amount}}';
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString('en-US'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toLocaleString('en-US'));
  }
  S.formatMoney = formatMoney;

  function toast(message, isError) {
    const stack = $('#ToastStack');
    if (!stack) return;
    const el = document.createElement('div');
    el.className = 'toast' + (isError ? ' toast--error' : '');
    el.setAttribute('role', 'status');
    el.textContent = message;
    stack.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 300);
    }, 3200);
  }
  S.toast = toast;

  /* ---------- focus trap + overlay controller -------------------------- */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class Overlay {
    constructor(el) {
      this.el = el;
      this.lastFocus = null;
      this.onKeydown = this.onKeydown.bind(this);
      $$('[data-overlay-close]', el).forEach((b) => b.addEventListener('click', () => this.close()));
      const overlay = $('.drawer__overlay, .modal__overlay', el);
      if (overlay) overlay.addEventListener('click', () => this.close());
    }
    open() {
      this.lastFocus = document.activeElement;
      this.el.classList.add('is-open');
      this.el.removeAttribute('aria-hidden');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      const first = $(FOCUSABLE, this.el);
      if (first) setTimeout(() => first.focus(), 60);
      this.el.dispatchEvent(new CustomEvent('overlay:open'));
    }
    close() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      this.el.dispatchEvent(new CustomEvent('overlay:close'));
    }
    onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, this.el).filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  S.Overlay = Overlay;

  const overlays = {};
  function registerOverlays() {
    $$('[data-overlay]').forEach((el) => {
      const id = el.getAttribute('data-overlay');
      if (!overlays[id]) overlays[id] = new Overlay(el);
    });
    $$('[data-overlay-open]').forEach((btn) => {
      if (btn.dataset.overlayBound) return;
      btn.dataset.overlayBound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const id = btn.getAttribute('data-overlay-open');
        if (overlays[id]) overlays[id].open();
      });
    });
  }
  S.openOverlay = (id) => overlays[id] && overlays[id].open();
  S.closeOverlay = (id) => overlays[id] && overlays[id].close();

  /* ---------- sticky header ------------------------------------------- */
  function initStickyHeader() {
    const wrapper = $('.header-wrapper--sticky');
    if (!wrapper) return;
    const onScroll = () => wrapper.classList.toggle('is-stuck', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- desktop mega menu --------------------------------------- */
  function initNav() {
    const items = $$('.nav__item--has-menu');
    items.forEach((item) => {
      const trigger = $('.nav__link', item);
      const menu = $('.mega-menu', item);
      if (!trigger || !menu) return;
      let closeTimer;

      const open = () => {
        clearTimeout(closeTimer);
        items.forEach((i) => { if (i !== item) close(i); });
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const close = (target) => {
        const node = target || item;
        node.classList.remove('is-open');
        const t = $('.nav__link', node);
        if (t) t.setAttribute('aria-expanded', 'false');
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => close(), 140); });
      trigger.addEventListener('click', (e) => {
        // Allow keyboard/tap users to toggle; the link still works on second activation
        if (!item.classList.contains('is-open')) { e.preventDefault(); open(); }
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close();
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { close(); trigger.focus(); }
      });
    });
  }

  /* ---------- mobile nav accordions ------------------------------------ */
  function initMobileNav() {
    $$('.mobile-nav__link[aria-expanded]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.classList.toggle('is-open', !expanded);
      });
    });
  }

  /* ---------- accordions (FAQ, product, facets) ------------------------ */
  function initAccordions(root) {
    $$('[data-accordion-trigger]', root).forEach((trigger) => {
      if (trigger.dataset.bound) return;
      trigger.dataset.bound = '1';
      trigger.addEventListener('click', () => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (!panel) return;
        if (panel.classList.contains('accordion__panel')) {
          panel.dataset.open = String(!expanded);
        } else {
          panel.hidden = expanded;
        }
      });
    });
  }
  S.initAccordions = initAccordions;

  /* ---------- carousels ------------------------------------------------ */
  function initCarousels(root) {
    $$('[data-carousel]', root).forEach((carousel) => {
      if (carousel.dataset.bound) return;
      carousel.dataset.bound = '1';
      const viewport = $('.carousel__viewport', carousel);
      const prev = $('[data-carousel-prev]', carousel);
      const next = $('[data-carousel-next]', carousel);
      if (!viewport) return;

      const step = () => {
        const slide = $('.carousel__slide', viewport);
        return slide ? slide.getBoundingClientRect().width + 16 : viewport.clientWidth * 0.8;
      };
      const update = () => {
        const max = viewport.scrollWidth - viewport.clientWidth - 2;
        if (prev) prev.disabled = viewport.scrollLeft <= 2;
        if (next) next.disabled = viewport.scrollLeft >= max;
      };
      if (prev) prev.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      if (next) next.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      viewport.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }
  S.initCarousels = initCarousels;

  /* ---------- scroll animations ---------------------------------------- */
  function initAnimations() {
    if (reduceMotion || !document.body.classList.contains('anim-enabled')) return;
    const targets = $$('[data-animate]:not(.is-visible)');
    if (!targets.length || !('IntersectionObserver' in window)) {
      targets.forEach((t) => t.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    targets.forEach((t) => io.observe(t));
  }
  S.initAnimations = initAnimations;

  /* ---------- lightbox -------------------------------------------------- */
  function initLightbox() {
    const box = $('#Lightbox');
    if (!box) return;
    const img = $('.lightbox__img', box);
    let group = [];
    let index = 0;

    const show = (i) => {
      if (!group.length) return;
      index = (i + group.length) % group.length;
      const src = group[index].getAttribute('data-lightbox-src') || group[index].querySelector('img')?.src;
      if (src) { img.src = src; img.alt = group[index].querySelector('img')?.alt || ''; }
    };
    const open = (trigger) => {
      group = $$('[data-lightbox]').filter((n) => n.getAttribute('data-lightbox') === trigger.getAttribute('data-lightbox'));
      show(group.indexOf(trigger));
      box.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      $('.lightbox__close', box).focus();
    };
    const close = () => {
      box.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
    };

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox]');
      if (trigger) { e.preventDefault(); open(trigger); }
    });
    $('.lightbox__close', box).addEventListener('click', close);
    $('.lightbox__nav--prev', box).addEventListener('click', () => show(index - 1));
    $('.lightbox__nav--next', box).addEventListener('click', () => show(index + 1));
    box.addEventListener('click', (e) => { if (e.target === box) close(); });
    document.addEventListener('keydown', (e) => {
      if (!box.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });
  }

  /* ---------- before / after slider ------------------------------------ */
  function initBeforeAfter() {
    $$('[data-before-after]').forEach((el) => {
      const range = $('.before-after__range', el);
      const after = $('.before-after__after', el);
      const handle = $('.before-after__handle', el);
      if (!range || !after || !handle) return;
      const apply = () => {
        const v = Number(range.value);
        after.style.clipPath = `inset(0 0 0 ${v}%)`;
        handle.style.left = v + '%';
      };
      range.addEventListener('input', apply);
      apply();
    });
  }

  /* ---------- product gallery ------------------------------------------ */
  function initGallery(root) {
    $$('[data-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = '1';
      const slides = $$('[data-gallery-slide]', gallery);
      const thumbs = $$('[data-gallery-thumb]', gallery);
      const main = $('.product-gallery__main', gallery);

      const prevBtn = $('[data-gallery-prev]', gallery);
      const nextBtn = $('[data-gallery-next]', gallery);
      const counter = $('[data-gallery-counter]', gallery);
      const thumbStrip = $('.product-gallery__thumbs', gallery);

      // Media belonging to a variant stays out of the gallery until that
      // variant is selected, so counting and stepping run over what is
      // actually on screen, not over every media the product owns.
      const isVariantMedia = (el) => el.hasAttribute('data-variant-media');
      const visibleSlides = () => slides.filter((s) => !s.hidden);

      let variantMediaId = null;
      const initialActive = slides.find((s) => s.classList.contains('is-active'));
      if (initialActive && isVariantMedia(initialActive)) {
        variantMediaId = initialActive.dataset.gallerySlide;
      }

      const applyVariantVisibility = () => {
        slides.forEach((s) => {
          if (isVariantMedia(s)) s.hidden = s.dataset.gallerySlide !== variantMediaId;
        });
        thumbs.forEach((t) => {
          if (isVariantMedia(t)) t.hidden = t.dataset.galleryThumb !== variantMediaId;
        });
      };

      const currentIndex = () => Math.max(0, visibleSlides().findIndex((s) => s.classList.contains('is-active')));

      const select = (id) => {
        slides.forEach((s) => s.classList.toggle('is-active', s.dataset.gallerySlide === id));
        thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.galleryThumb === id)));

        const shown = visibleSlides();
        const i = shown.findIndex((s) => s.dataset.gallerySlide === id);
        if (i < 0) return;
        if (counter) counter.textContent = (i + 1) + '/' + shown.length;
        if (prevBtn) prevBtn.disabled = i === 0;
        if (nextBtn) nextBtn.disabled = i === shown.length - 1;

        // Keep the active thumbnail visible — the strip scrolls once there are
        // more thumbs than fit, and otherwise the current one drifts off-screen.
        const activeThumb = thumbs[i];
        if (activeThumb && thumbStrip && thumbStrip.scrollWidth > thumbStrip.clientWidth) {
          const t = activeThumb.offsetLeft;
          const w = activeThumb.offsetWidth;
          const view = thumbStrip.scrollLeft;
          if (t < view || t + w > view + thumbStrip.clientWidth) {
            thumbStrip.scrollTo({ left: t - (thumbStrip.clientWidth - w) / 2, behavior: 'smooth' });
          }
        }
      };

      const step = (delta) => {
        const shown = visibleSlides();
        const next = currentIndex() + delta;
        if (next < 0 || next >= shown.length) return;
        select(shown[next].dataset.gallerySlide);
      };

      thumbs.forEach((t) => t.addEventListener('click', () => select(t.dataset.galleryThumb)));

      // Fired by the variant picker: swap which variant photo is on show, then
      // jump to it.
      gallery.addEventListener('gallery:select', (e) => {
        const id = String(e.detail.id);
        if (slides.some((s) => s.dataset.gallerySlide === id && isVariantMedia(s))) {
          variantMediaId = id;
          applyVariantVisibility();
        }
        select(id);
      });
      if (prevBtn) prevBtn.addEventListener('click', () => step(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => step(1));

      if (slides.length > 1) {
        gallery.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        });

        // Swipe on touch devices, where the arrows are a small target.
        let startX = null;
        const mainEl = $('.product-gallery__main', gallery);
        if (mainEl) {
          mainEl.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
          mainEl.addEventListener('touchend', (e) => {
            if (startX === null) return;
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
            startX = null;
          }, { passive: true });
        }

        // Sync visibility, then the arrows/counter, with whatever starts active.
        applyVariantVisibility();
        const active = slides.find((s) => s.classList.contains('is-active')) || visibleSlides()[0] || slides[0];
        select(active.dataset.gallerySlide);
      }

      const zoomBtn = $('[data-gallery-zoom]', gallery);
      if (zoomBtn && main) {
        zoomBtn.addEventListener('click', () => main.classList.toggle('is-zoomed'));
        main.addEventListener('mousemove', (e) => {
          if (!main.classList.contains('is-zoomed')) return;
          const r = main.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * 100;
          const y = ((e.clientY - r.top) / r.height) * 100;
          const activeImg = $('.is-active img', main);
          if (activeImg) activeImg.style.transformOrigin = `${x}% ${y}%`;
        });
        main.addEventListener('click', (e) => {
          if (main.classList.contains('is-zoomed') && !e.target.closest('[data-gallery-zoom]')) {
            main.classList.remove('is-zoomed');
          }
        });
      }
    });
  }
  S.initGallery = initGallery;

  /* ---------- sticky add to cart --------------------------------------- */
  function initStickyAtc() {
    const bar = $('[data-sticky-atc]');
    const anchor = $('[data-sticky-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0)),
      { threshold: 0 }
    );
    io.observe(anchor);
  }

  /* ---------- view toggle (collection grid/list) ------------------------ */
  function initViewToggle() {
    const grid = $('[data-product-grid]');
    if (!grid) return;
    const stored = localStorage.getItem('spooky:view');
    const apply = (view) => {
      grid.classList.toggle('product-grid--list', view === 'list');
      $$('[data-view-toggle]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewToggle === view)));
      localStorage.setItem('spooky:view', view);
    };
    if (stored) apply(stored);
    $$('[data-view-toggle]').forEach((b) => b.addEventListener('click', () => apply(b.dataset.viewToggle)));
  }

  /* ---------- newsletter inline feedback -------------------------------- */
  function initNewsletter() {
    $$('[data-newsletter-form]').forEach((form) => {
      form.addEventListener('submit', () => {
        const btn = $('button[type="submit"]', form);
        if (btn) { btn.classList.add('btn--loading'); btn.disabled = true; }
      });
    });
  }

  /* ---------- boot ------------------------------------------------------ */
  function init(root) {
    registerOverlays();
    initAccordions(root);
    initCarousels(root);
    initGallery(root);
    initAnimations();
  }
  S.init = init;

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    initStickyHeader();
    initNav();
    initMobileNav();
    initLightbox();
    initBeforeAfter();
    initStickyAtc();
    initViewToggle();
    initNewsletter();
  });

  // Re-run bindings when the Theme Editor swaps a section
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    initBeforeAfter();
    initStickyAtc();
  });

  window.Spooky = S;
})();
